
import {
  computed, defineComponent, onBeforeMount, onMounted, onUnmounted, ref, nextTick, reactive,
} from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { Swiper, SwiperSlide } from 'swiper/vue/swiper-vue';
import { Lazy, Mousewheel } from 'swiper';
import { onClickOutside } from '@vueuse/core';
import { useDriveStore } from '@/modules/drive/store/driveStore';
import { useMainStore } from '@/store/mainStore';
import { useAlertsStore } from '@/modules/alerts/store/alertsStore';
import AppCropper from '@/components/AppCropper.vue';
import MediaSettingsPopUp from '@/components/BottomPopUp/PopUpTypes/Settings/MediaSettingsPopUp.vue';
import MoveToPopUp from '@/components/BottomPopUp/PopUpTypes/MoveTo/MoveToPopUp.vue';
import SharePopUp from '@/components/BottomPopUp/PopUpTypes/Share/SharePopUp.vue';
import AppMediaViewSlide from '@/components/AppMediaView/AppMediaViewSlide.vue';
import AppMediaViewHeader from '@/components/AppMediaView/AppMediaViewHeader.vue';
import AppMediaViewNav from '@/components/AppMediaView/AppMediaViewNav.vue';
import AppQuestionPopUp from '@/components/AppQuestionPopUp.vue';
import AppLabelInput from '@/components/UI/AppLabelInput.vue';
import AppSkeletonRect from '@/components/skeletons/AppSkeletonRect.vue';
import AppRenameFile from '@/components/AppRenameFile.vue';
import { ITEMS_ON_PAGE } from '@/types/consts';

import {
  BOTTOM_POPUP_TYPES, MEDIA_TYPES, NAVIGATION_PAGES, SHARE_ACCESS, SHARE_TYPES,
} from '@/types/enums';
import {
  ICase,
  ICaseFilesManage,
  ICropCoordinates,
  IImageCrop,
  ILabelInput,
  IMedia,
  ISharedLable,
  ITagInput,
} from '@/types/interfaces';
import clearPhotoCache from '@/helpers/clearPhotoCache';
import UploadService from '@/services/UploadService';

import 'swiper/swiper-bundle.css';
import DriveService from '@/modules/drive/services/DriveService';
import AppSoloCaseSelector from '@/components/AppSoloCaseSelector.vue';
import AppCreateCasePopUp from '@/components/AppCreateCasePopUp.vue';
import AppSkeletonCaseView from '@/components/skeletons/AppSkeletonCaseView.vue';
import AppNetworkConfirmPopUp from '@/components/AppNetworkConfirmPopUp.vue';

export default defineComponent({
  components: {
    AppNetworkConfirmPopUp,
    AppSkeletonCaseView,
    AppSoloCaseSelector,
    AppCreateCasePopUp,
    AppCropper,
    MediaSettingsPopUp,
    MoveToPopUp,
    SharePopUp,
    Swiper,
    SwiperSlide,
    AppMediaViewSlide,
    AppMediaViewHeader,
    AppMediaViewNav,
    AppQuestionPopUp,
    AppLabelInput,
    AppSkeletonRect,
    AppRenameFile,
  },
  emits: ['on-close', 'close'],
  props: {
    viewOnly: { type: Boolean, default: false },
  },
  setup() {
    const mySwiper = ref();
    const mainStore = useMainStore();
    const driveStore = useDriveStore();
    const alertsStore = useAlertsStore();
    const router = useRouter();
    const route = useRoute();

    const isMixedView = ref(false);
    const initialSlide = ref(0);
    const currentMedia = ref(0);
    const showSkeleton = ref(false);

    const isMediaSettingsPopUp = ref(false);
    const isSharePopUp = ref(false);
    const isMoveToPopUp = ref(false);
    const isQuestionPopUp = ref(false);
    const caseViewSkeleton = ref(false);
    const caseSelector = ref(false);
    const pauseVideo = ref(false);
    const fileRenamePopUp = ref(false);
    const renameMountErrorInfo = ref<undefined | {title: string, error: string}>();

    const updateMediaOrderContentIds = ref<string[]>([]);

    // Label popup
    const labelPopup = ref();
    const showAddLabel = ref(false);
    function addLabelsOpen() {
      if (driveStore.appPhotoViewMedia?.labels?.length) {
        mainStore.inputPhotoTags = driveStore.appPhotoViewMedia?.labels as unknown as ITagInput[];
      }
      showAddLabel.value = true;
    }

    function updateMediaOrder(contentId: string) {
      const photoInPhotoList = driveStore.photos.data.find((photo) => photo.contentId === contentId);
      if (photoInPhotoList) {
        driveStore.photos.data = [photoInPhotoList, ...driveStore.photos.data.filter((photo) => photo.contentId !== contentId)];
      }

      driveStore.cases.data.map((cas) => {
        const photoInCase = cas.photos.find((photo) => photo.contentId === contentId);
        if (photoInCase) {
          return ({
            ...cas,
            photos: [photoInCase, ...cas.photos.filter((photo) => photo.contentId !== contentId)],
          });
        }
        const fileInCase = cas.files.find((file) => file.contentId === contentId);
        if (fileInCase) {
          return ({
            ...cas,
            files: [fileInCase, ...cas.files.filter((photo) => photo.contentId !== contentId)],
          });
        }
        return cas;
      });

      if (driveStore.currentCase) {
        const photoInCase = driveStore.currentCase.photos.find((ph) => ph.contentId === contentId);
        if (photoInCase) {
          driveStore.currentCase.photos = [photoInCase, ...driveStore.currentCase.photos.filter((photo) => photo.contentId !== contentId)];
        }
        const fileInCase = driveStore.currentCase.files.find((ph) => ph.contentId === contentId);
        if (fileInCase) {
          driveStore.currentCase.files = [fileInCase, ...driveStore.currentCase.files.filter((photo) => photo.contentId !== contentId)];
        }
      }

      if (driveStore.currentFolder && driveStore.currentFolder.mixedData) {
        const mediaInFolder = driveStore.currentFolder.mixedData.find((mix) => mix.file?.contentId === contentId);
        if (mediaInFolder) {
          driveStore.currentFolder.mixedData = [mediaInFolder, ...driveStore.currentFolder.mixedData.filter((mix) => mix.file?.contentId !== contentId)];
        }
      }

      const mediaInMixed = driveStore.mixedData.data.find((mix) => mix.file?.contentId === contentId);
      if (mediaInMixed) {
        driveStore.mixedData.data = [mediaInMixed, ...driveStore.mixedData.data.filter((mix) => mix.file?.contentId !== contentId)];
      }
    }

    function updateMediaLabels(labels: ILabelInput[]) {
      const { contentId } = driveStore.appPhotoViewMedia!;
      updateMediaOrderContentIds.value.push(contentId);

      const photoInPhotoList = driveStore.photos.data.find((photo) => photo.contentId === contentId);
      if (photoInPhotoList) {
        photoInPhotoList.labels = labels;
      }

      driveStore.cases.data.forEach((cas) => {
        const photoInCase = cas.photos.find((photo) => photo.contentId === contentId);
        if (photoInCase) {
          photoInCase.labels = labels;
        }
        const fileInCase = cas.files.find((file) => file.contentId === contentId);
        if (fileInCase) {
          fileInCase.labels = labels;
        }
      });

      if (driveStore.currentCase) {
        const photoInCase = driveStore.currentCase.photos.find((ph) => ph.contentId === contentId);
        if (photoInCase) {
          photoInCase.labels = labels;
          driveStore.cases.data = [driveStore.currentCase, ...driveStore.cases.data.filter((cas) => cas.id !== driveStore.currentCase.id)];
        }
        const fileInCase = driveStore.currentCase.files.find((ph) => ph.contentId === contentId);
        if (fileInCase) {
          fileInCase.labels = labels;
          driveStore.cases.data = [driveStore.currentCase, ...driveStore.cases.data.filter((cas) => cas.id !== driveStore.currentCase.id)];
        }
      }

      if (driveStore.currentFolder && driveStore.currentFolder.mixedData) {
        const mediaInFolder = driveStore.currentFolder.mixedData.find((mix) => mix.file?.contentId === contentId);
        if (mediaInFolder) {
          mediaInFolder.file!.labels = labels;
        }
      }

      const mediaInMixed = driveStore.mixedData.data.find((mix) => mix.file?.contentId === contentId);
      if (mediaInMixed) {
        mediaInMixed.file!.labels = labels;
      }
    }

    function addLabelsClose() {
      const ids = [] as number[];
      mainStore.inputPhotoViewTags.forEach((el) => {
        const elm = mainStore.recentInputLabels.find(({ name }) => name === el.text);
        if (elm) { ids.push(elm.id); }
      });
      const payload = {
        id: driveStore.appPhotoViewMedia?.id as string,
        labelIds: ids,
        photoCrop: null,
      };
      showAddLabel.value = false;
      DriveService.addImageLabels(payload).then((response) => {
        if (response.success) {
          const { labels } = response.results.data;
          driveStore.appPhotoViewMedia!.labels = labels;
          updateMediaLabels(labels);
          mainStore.inputPhotoTags = [];
        }
      }).finally(() => {
        mainStore.inputPhotoTags = [];
        showAddLabel.value = false;
      });
    }

    function cancelLabelsPopup() {
      mainStore.inputPhotoTags = [];
      showAddLabel.value = false;
    }

    function openLabelsPopup() {
      // if (driveStore.appPhotoViewMedia?.labels?.length) {
      //   mainStore.inputPhotoTags = driveStore.appPhotoViewMedia?.labels as unknown as ITagInput[];
      // }
      showAddLabel.value = true;
    }

    onClickOutside(labelPopup, cancelLabelsPopup);
    // -- end -- Label popup

    const isNetwork = computed(() => route.meta.isNetwork || false);
    const navigationPage = computed(() => route.meta.navigationPage);
    const isCase = computed(
      () => navigationPage.value === NAVIGATION_PAGES.DRIVE_CASES
        || navigationPage.value === NAVIGATION_PAGES.NETWORK_CASES
        || navigationPage.value === NAVIGATION_PAGES.CASE_EDIT
        || navigationPage.value === NAVIGATION_PAGES.CASE_VIEW,
    );

    const currentPage = ref(0);
    const CURRENT_ROUTE_META = computed(() => route.meta.navigationPage || 'no value');

    const mediaList = computed(() => {
      if (route.meta.navigationPage === NAVIGATION_PAGES.DRIVE_PHOTOS || route.meta.navigationPage === NAVIGATION_PAGES.DRIVE || route.meta.navigationPage === NAVIGATION_PAGES.DRIVE_FILES) {
        return isMixedView.value ? driveStore.mixedData.data.filter((el) => el.type === 'FILE').map((el) => el.file) : driveStore.photos.data;
      }
      if (route.meta.navigationPage === NAVIGATION_PAGES.NETWORK || route.meta.navigationPage === NAVIGATION_PAGES.NETWORK_PHOTOS || route.meta.navigationPage === NAVIGATION_PAGES.NETWORK_CASES) {
        if (isCase.value && driveStore.currentCase.id) {
          return [...driveStore.photoList, ...driveStore.currentCase.files] || [];
        }
        return isMixedView.value
          ? driveStore.networkMixedData.data.filter((el) => el.type === 'FILE').map((el) => el.file)
          : driveStore.networkPhotos.data;
      }
      if (route.meta.navigationPage === NAVIGATION_PAGES.DRIVE_FOLDER) {
        return driveStore.currentFolder?.mixedData?.filter((el) => el.type === 'FILE').map((el) => el.file) || [];
      }
      if (route.meta.navigationPage === NAVIGATION_PAGES.ALERTS) {
        return alertsStore.notifications.data.filter((el) => el.type === 'SHARE_FILE' && el.sharedInfo !== null).map((el) => el.sharedInfo);
      }
      if (route.meta.navigationPage === NAVIGATION_PAGES.CASE_VIEW || route.meta.navigationPage === NAVIGATION_PAGES.CASE_EDIT) {
        return [...driveStore.currentCase.photos, ...driveStore.currentCase.files];
      }
      const isMessagePage = (route.meta.navigationPage === NAVIGATION_PAGES.MESSAGES_ID || route.meta.navigationPage === NAVIGATION_PAGES.MESSAGES_GROUP_CHAT_ID);
      if (isMessagePage && driveStore.currentCase.id) {
        return [...driveStore.currentCase.photos, ...driveStore.currentCase.files];
      }
      return [driveStore.appPhotoViewMedia];
    });
    const currentItem = computed(() => driveStore.appPhotoViewMedia);
    const currentItemType = computed(() => (currentItem.value?.type === MEDIA_TYPES.PHOTO ? MEDIA_TYPES.PHOTO : MEDIA_TYPES.FILES));
    const isPhoto = computed(() => currentItem.value?.type === MEDIA_TYPES.PHOTO);
    const isFile = computed(() => currentItem.value?.type === MEDIA_TYPES.FILES);

    function setCurrentPage() {
      currentPage.value = Math.floor(mediaList.value.length / ITEMS_ON_PAGE) - 1;
    }

    function setItemShared() {
      // is declared but it is not using now. Current code will not work correctly with new app logic
      // if (currentItemType.value === SHARE_TYPES.PHOTO) {
      //   driveStore.fullscreenPhoto!.shared = true;
      // }
      // if (currentItemType.value === SHARE_TYPES.FILE) {
      //   driveStore.fullscreenFile!.shared = true;
      // }
    }

    function setSharedLabels(labels: ISharedLable[]) {
      // is declared but it is not using now. Current code will not work correctly with new app logic
      // driveStore.fullscreenPhoto!.sharedLabels = labels;
    }

    const onSwiper = (swiper) => {
      const startMediaId = driveStore.appPhotoViewMedia?.contentId;

      let indexToGo = 0;
      if (startMediaId) {
        indexToGo = mediaList.value.map((el) => (el as IMedia)!.contentId).indexOf(startMediaId);
      }
      initialSlide.value = indexToGo;
      swiper.slideTo(indexToGo, 0, false);
    };

    const onSlideChange = (swiper) => {
      pauseVideo.value = true;
      const mediaType = (mediaList?.value[swiper.activeIndex] as IMedia)?.type;

      const mediaId = mediaList?.value[swiper.activeIndex]?.id;
      if (!mediaId || !mediaType) return;
      if (mediaType === MEDIA_TYPES.PHOTO) {
        // fetchPhoto(mediaId);
      }
      if (mediaType === MEDIA_TYPES.VIDEO) {
        // fetchPhoto(mediaId);
      }
      if (mediaType === MEDIA_TYPES.FILES) {
        // fetchFile(mediaId);
      }
      currentMedia.value = swiper.activeIndex;
      driveStore.appPhotoViewMedia = mediaList?.value[swiper.activeIndex] as IMedia;
      showAddLabel.value = false;
    };

    const notLoading = ref(false);
    function fetchData() {
      const resolveRequest = async () => {
        if (isNetwork.value) {
          return driveStore.fetchNetworkPhotos();
        }
        return driveStore.fetchPhotos();
      };
      return resolveRequest().then((response) => {
        driveStore.photoList = response;
        setCurrentPage();
        notLoading.value = false;
      });
    }

    const onReachEnd = (swiper) => {
      if (isCase.value && driveStore.currentCase.id) return;
      if (swiper.progress > 0 && !notLoading.value) {
        notLoading.value = true;
        if (currentPage.value < driveStore.photos.lastPage) {
          fetchData();
        }
      }
    };

    function openPopUp(type: BOTTOM_POPUP_TYPES) {
      if (isCase.value && isSharePopUp.value) {
        return;
      }
      mainStore.BottomPopUpType = type;
    }

    function openMovePopUp() {
      isMediaSettingsPopUp.value = false;
      isMoveToPopUp.value = true;
    }

    function openQuestionPopUp() {
      isMediaSettingsPopUp.value = false;
      isQuestionPopUp.value = true;
    }

    function deletePhoto() {
      if (isCase.value) {
        const removeFilePayload = {
          id: driveStore.currentCase.id!,
          fileIds: [driveStore.appPhotoViewMedia!.id],
        } as ICaseFilesManage;

        let idx = driveStore.photoList.findIndex((photo) => photo.id === driveStore.appPhotoViewMedia?.id);
        driveStore.removeFilesFromCase(removeFilePayload);

        if (driveStore.photoList.length >= 1) {
          idx = (idx + 1) % driveStore.photoList.length;
          driveStore.appPhotoViewMedia = driveStore.photoList[idx];
          isMediaSettingsPopUp.value = false;
          isQuestionPopUp.value = false;
        } else {
          driveStore.appPhotoViewMedia = undefined;
        }
      } else {
        isQuestionPopUp.value = false;
        driveStore.deleteMedia(driveStore.appPhotoViewMedia!.id, MEDIA_TYPES.PHOTO);
        driveStore.appPhotoViewMedia = undefined;
      }
    }

    function saveMediaAsCopy() {
      showSkeleton.value = true;
      isMediaSettingsPopUp.value = false;
      driveStore.saveMediaAsCopy(driveStore.appPhotoViewMedia!.id).then((response) => {
        if (response.success) {
          driveStore.appPhotoViewMedia = undefined;
          if (isNetwork.value) {
            router.push('/drive').then(() => {
              nextTick(() => {
                driveStore.appPhotoViewMedia = { ...response.results.data as IMedia, isMixed: true };
                showSkeleton.value = false;
              });
            });
          } else {
            nextTick(() => {
              driveStore.appPhotoViewMedia = { ...response.results.data as IMedia, isMixed: true };
              showSkeleton.value = false;
            });
          }
        }
      });
    }

    const confirmUnsharing = ref(false);
    const caseToUnshare = ref({} as ICase);
    function updateBrowserCache(mediaObj: any) {
      let photoInPhotoList;
      if (Object.prototype.hasOwnProperty.call(mediaObj, 'contentId')) {
        photoInPhotoList = driveStore.photos.data.find((photo) => photo.contentId === mediaObj.contentId);
      }
      if (photoInPhotoList) {
        clearPhotoCache(photoInPhotoList);
        driveStore.photos.data = [photoInPhotoList, ...driveStore.photos.data.filter((photo) => photo.contentId !== mediaObj!.contentId)];
      }

      driveStore.cases.data.map((cas) => {
        const photoInCase = cas.photos.find((photo) => photo.contentId === mediaObj!.contentId);
        if (photoInCase) {
          clearPhotoCache(photoInCase);
          return ({
            ...cas,
            photos: [photoInCase, ...cas.photos.filter((photo) => photo.contentId !== mediaObj?.contentId)],
          });
        }
        return cas;
      });

      if (driveStore.currentCase) {
        const photoInCase = driveStore.currentCase.photos.find((ph) => ph.contentId === mediaObj!.contentId);
        if (photoInCase) {
          clearPhotoCache(photoInCase);
          driveStore.currentCase.photos = [photoInCase, ...driveStore.currentCase.photos.filter((photo) => photo.contentId !== mediaObj!.contentId)];

          driveStore.cases.data = [driveStore.currentCase, ...driveStore.cases.data.filter((cas) => cas.id !== driveStore.currentCase.id)];
        }
      }

      if (driveStore.currentFolder && driveStore.currentFolder.mixedData) {
        const photoInFolder = driveStore.currentFolder.mixedData.find((mix) => mix.file?.contentId === mediaObj!.contentId);
        if (photoInFolder) {
          clearPhotoCache(photoInFolder.file!);
          // eslint-disable-next-line max-len
          driveStore.currentFolder.mixedData = [photoInFolder, ...driveStore.currentFolder.mixedData.filter((mix) => mix.file?.contentId !== mediaObj!.contentId)];
        }
      }

      const photoInMixed = driveStore.mixedData.data.find((mix) => mix.file?.contentId === mediaObj!.contentId);
      if (photoInMixed) {
        clearPhotoCache(photoInMixed.file!);
        driveStore.mixedData.data = [photoInMixed, ...driveStore.mixedData.data.filter((mix) => mix.file?.contentId !== mediaObj!.contentId)];
      }
    }
    function removeMediaFromSharing() {
      const id = driveStore.appPhotoViewMedia?.id as unknown as string;
      const networkCases = [...driveStore.networkCases?.data];

      let existInCase = false;
      // const caseToUnshare = {} as ICase;

      for (let i = 0; i < networkCases.length; i += 1) {
        const photoExist = (networkCases[i] as ICase).photos.filter((el: IMedia) => el.id === id);
        const filesExist = (networkCases[i] as ICase).files.filter((el: IMedia) => el.id === id);
        if (photoExist.length || filesExist.length) {
          existInCase = true;
          caseToUnshare.value = networkCases[i];
          break;
        }
      }
      if (existInCase) {
        confirmUnsharing.value = true;
        return null;
      }
      if (id) {
        const selectedMedia = {
          ids: [id as string],
          accessType: SHARE_ACCESS.SPECIFIC_PEOPLE,
          target: '',
        };
        DriveService.removeFromSharing(selectedMedia).then(() => {
          driveStore.fetchNetworkPhotos(true).then(() => {
            driveStore.appPhotoViewMedia = undefined;
            driveStore.networkMixedData.data = driveStore.networkMixedData.data.filter((selectedMediaItem) => selectedMediaItem.id !== id);
          });
        });
      }
      return null;
    }

    async function unshareCase() {
      const caseId = caseToUnshare.value.id as string;
      const id = driveStore.appPhotoViewMedia?.id as unknown as string;

      if (caseId) {
        const selectedCase = {
          ids: [caseId as string],
          accessType: SHARE_ACCESS.SPECIFIC_PEOPLE,
          target: '',
        };
        const selectedMedia = {
          ids: [id as string],
          accessType: SHARE_ACCESS.SPECIFIC_PEOPLE,
          target: '',
        };

        const unshareIdx = driveStore.networkCases?.data.findIndex((el) => el.id === caseToUnshare.value.id);

        // eslint-disable-next-line no-unused-expressions
        driveStore.networkCases?.data.splice(unshareIdx, 1);

        try {
          driveStore.networkPhotos.page = 0;
          driveStore.networkPhotos.lastPage = -1;
          driveStore.networkMixedData.page = 0;
          driveStore.networkMixedData.lastPage = -1;
          await DriveService.removeFromSharing(selectedCase);
          await driveStore.fetchNetworkCases();
          await driveStore.fetchNetworkPhotos();
          await driveStore.fetchNetworkMixedData(false).then((res) => {
            console.log(res);
          });
          driveStore.appPhotoViewMedia = undefined;
        } catch (error) {
          console.log(error);
        }
      }
    }
    function updateLocation(folderId: string | null) {
      driveStore.appPhotoViewMedia!.folderId = folderId;
    }

    // Cropper functionality
    const image = ref({} as IImageCrop);
    const cropperRef = ref();
    const loader = ref('');

    function openCropper() {
      const fileType = driveStore.appPhotoViewMedia!.name.split('.').pop();
      image.value = {
        url: process.env.VUE_APP_MEDIA_URL + driveStore.appPhotoViewMedia!.fileUrl,
        fileName: driveStore.appPhotoViewMedia!.name,
        fileType: `image/${fileType}`,
      } as IImageCrop;

      cropperRef.value.loader = 'Loading image cropper';
    }

    function handleOnCrop(coordinates: ICropCoordinates) {
      loader.value = 'Saving cropped image';
      const mediaObj = JSON.parse(JSON.stringify(driveStore.appPhotoViewMedia));
      image.value = {} as IImageCrop;
      driveStore.appPhotoViewMedia = undefined;
      UploadService.cropPhoto(mediaObj!.id, coordinates).then(() => {
        // NotificationService.notifySuccess();
        image.value = {} as IImageCrop;
        updateBrowserCache(mediaObj);
        driveStore.appPhotoViewMedia = undefined;
      }).finally(() => {
        loader.value = '';
      });
    }

    const createCaseModal = ref();
    function createNewCase() {
      createCaseModal.value.isShow = true;
    }
    function openCaseSelector() {
      caseSelector.value = true;
    }

    const skeletonCase = reactive({ title: '', photosAmount: 0, filesAmount: 0 });
    function addMediaToCase(iCase: ICase) {
      caseViewSkeleton.value = true;

      skeletonCase.title = iCase.title;
      skeletonCase.filesAmount = iCase.filesAmount || 0;
      skeletonCase.photosAmount = (iCase.photosAmount || 0) + 1;

      driveStore.currentCase.title = iCase.title;
      driveStore.currentCase.photosAmount = skeletonCase.photosAmount;
      driveStore.currentCase.filesAmount = skeletonCase.filesAmount;

      const payload = { id: iCase.id!, fileIds: [driveStore.appPhotoViewMedia?.id as unknown as string] };
      driveStore.addFilesToCase(payload).then((response) => {
        if (response.success) {
          router.push(`/drive/case/my/${iCase.id!}/view`).then(() => {
            // emit('close-media');
            driveStore.appPhotoViewMedia = undefined;
          });
        }
      }).catch(() => {
        caseViewSkeleton.value = false;
      });
    }

    onBeforeMount(() => {
      isMixedView.value = driveStore.appPhotoViewMedia?.isMixed || false;
    });

    onMounted(() => {
      setCurrentPage();
    });

    onUnmounted(() => {
      updateMediaOrderContentIds.value.forEach((contentId) => updateMediaOrder(contentId));
    });

    return {
      CURRENT_ROUTE_META,
      isMediaSettingsPopUp,
      currentMedia,
      mediaList,
      route,
      isNetwork,
      isCase,
      openPopUp,
      BOTTOM_POPUP_TYPES,
      MEDIA_TYPES,
      SHARE_TYPES,
      driveStore,
      isPhoto,
      isFile,
      showSkeleton,
      caseViewSkeleton,
      caseSelector,
      createCaseModal,
      skeletonCase,
      createNewCase,
      openCaseSelector,
      addMediaToCase,

      // share popup
      currentItem,
      currentItemType,
      setItemShared,
      setSharedLabels,

      // Cropper functionality
      cropperRef,
      image,
      loader,
      openCropper,
      handleOnCrop,

      // settings
      isMoveToPopUp,
      isSharePopUp,
      isQuestionPopUp,
      fileRenamePopUp,
      openQuestionPopUp,
      openMovePopUp,
      updateLocation,
      deletePhoto,
      saveMediaAsCopy,
      removeMediaFromSharing,
      confirmUnsharing,
      caseToUnshare,
      unshareCase,

      mySwiper,
      initialSlide,
      onSwiper,
      onSlideChange,
      modules: [Mousewheel, Lazy],
      onReachEnd,
      currentPage,
      isMixedView,
      openLabelsPopup,

      showAddLabel,
      labelPopup,
      addLabelsOpen,
      cancelLabelsPopup,
      addLabelsClose,
      pauseVideo,
      renameMountErrorInfo,
    };
  },
});
